import { Component, DestroyRef, ViewChild, Input, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { skip } from 'rxjs';
import { NursingAuditService } from 'src/app/services/nursing-audit.service';
import { AuthService } from 'src/app/services/auth.service';
import { Table } from "primeng/table";
import { SelectItem } from 'primeng/api';
import { environment } from 'src/environments/environment';
import { LocationSelectComponent } from 'src/app/shared/location-select/location-select.component';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { LocationAccessService } from 'src/app/services/location-access.service';
import { ThemeColorService } from 'src/app/services/theme-color.service';
import { ThemePreferenceService } from 'src/app/services/theme-preference.service';

import { UrcComponent } from "src/app/variance/urc/urc.component";

const MEDICATION_CHART_COLOR_TOKENS = [
  '--chart-medication-series-1',
  '--chart-medication-series-2',
  '--chart-medication-series-3',
  '--chart-medication-series-4',
  '--chart-medication-series-5',
  '--chart-medication-series-6',
  '--chart-medication-series-7',
  '--chart-medication-series-8',
  '--chart-medication-series-9',
  '--chart-medication-series-10'
] as const;

@Component({
    selector: 'app-medication-insights',
    templateUrl: './medication-insights.component.html',
    styleUrls: ['./medication-insights.component.scss'],
    standalone: false
})
export class MedicationInsightsComponent implements OnInit {
  private auditService = inject(NursingAuditService);
  authService = inject(AuthService);
  private locationAccessService = inject(LocationAccessService);
  private themeColorService = inject(ThemeColorService);
  private themePreferenceService = inject(ThemePreferenceService);
  private destroyRef = inject(DestroyRef);
  private latestChartResponse: any;

  loading = true;
  loc_filter_breakdown= ''
  footprint:string = environment.footprint
  filter:any = {
    duration: 90,
    ...this.locationAccessService.buildAuthorizedFilters(
      this.locationAccessService.buildInitialFilters(this.authService.user),
      this.authService.user
    ),
    ServiceGroup: "All",
    medOptions: "All" // need it for the med group multiSelect
  }
  selectedDays = 90;
  days: SelectItem[] = [
    { label: '90 Days', value: 90 },
    { label: '30 Days', value: 30 },
    { label: '7 Days', value: 7 }
  ];
  date: any = {}
  locationSelectFilter: any = {
    region:[], 
    campus:[], 
    facility:[], 
    changeValue: true
  };
  auditorSelection:any = {}
  @ViewChild("medGroupT") medGroupT!: Table;
  @ViewChild("locationSelectComp") locationSelectComp!: LocationSelectComponent;
  @ViewChild('varianceComponent') varianceComp!: UrcComponent;
  @Input() deId=false;
  medicationGrouping: any[] = [];
  verifiedOverView:any[] = [];
  unaccountedOverView:any[] = [];
  highRiskOverView:any[] = [];
  occurancecOverView:any[] = [];
  medGroupOptions: any[] = [];
  totalOccurrances:any;
  occuranceOptions: any = {};
  highRiskPerc: any;
  highRiskOptions:any = {}
  unaccountedMed: any;
  unaccountedOptions: any = {};
  verifiedCount: any;
  verifiedOptions: any = {};
  plugin = ChartDataLabels;
  filterTable = false;
  selectedVerifiedRow: any = null;
  rowFilter: any = {};
  selectedRow:any;

  constructor() {
    this.themePreferenceService.preference$
      .pipe(skip(1), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.refreshChartTheme());
  }

  private refreshChartTheme(): void {
    if (!this.latestChartResponse) {
      return;
    }

    this.loadOccuranceChart(this.latestChartResponse);
    this.loadHighRiskChart(this.latestChartResponse);
    this.loadUnaccountedChart(this.latestChartResponse);
    this.loadVerifiedChart(this.latestChartResponse);
  }
  ngOnInit(){
     if (this.footprint == 'small'){
      this.loc_filter_breakdown = 'Facility'
     }else{
      this.loc_filter_breakdown = 'Region'
     }

    this.locationSelectFilter = this.locationAccessService.buildInitialFilters(this.authService.user, {
      footprint: this.footprint,
      changeValue: true
    });
    // Set initial sort for Medication Grouping table by unaccountedCount descending
    this.filter.$sortField = 'unaccountedCount';
    this.filter.$sortOrder = -1;
    this.loadMedOptions();
  }

  refreshData() {
    this.auditService.getMedicationInsights(this.filter).subscribe((res) =>{
      this.latestChartResponse = res;
      this.loadMedTable(res);
      this.getOverView(res);
      this.loadOccuranceChart(res);
      this.loadHighRiskChart(res);
      this.loadUnaccountedChart(res);
      this.loadVerifiedChart(res);
    })
  }

  loadMedTable(res: any) {
    this.medicationGrouping = res["medGroupTable"];
    this.loading = false;
  }
  getOverView(res: any){
    this.verifiedOverView = res["totalVerifiedCount"]
    this.unaccountedOverView = res["totalUnaccountedCount"]
    this.highRiskOverView = res["overallHighRiskPercent"].toPrecision(2)
    this.occurancecOverView = res["overallOccurances"]
  }

  private getMedicationChartTheme(): { palette: string[]; title: string; dataLabel: string } {
    return {
      palette: MEDICATION_CHART_COLOR_TOKENS.map((token) => this.themeColorService.resolve(token)),
      title: this.themeColorService.resolve('--text-primary'),
      dataLabel: this.themeColorService.resolve('--chart-data-label-text')
    };
  }

  loadOccuranceChart(res: any){
    const chartTheme = this.getMedicationChartTheme();
    if (Array.isArray(res["medGroupTable"])) {
      const top10MedGroups = res["medGroupTable"]
        .filter((item: any) => typeof item.occurrenceCount === 'number' && item.medGroupName)
        .sort((a: any, b: any) => b.occurrenceCount - a.occurrenceCount)
        .slice(0, 10);

      this.totalOccurrances = {
        labels: top10MedGroups.map((item: any) => String(item.medGroupName)),
        datasets: [
          {
            label: 'Total Count',
            data: top10MedGroups.map((item: any) => Number(item.occurrenceCount)),
            backgroundColor: chartTheme.palette.slice(0, top10MedGroups.length),
            borderWidth: 1
          }
        ]
      };
    }
    this.occuranceOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: false,
          position: 'right'
        },
        title: {
          display: true,
          text: 'Medication Groups by Total Count',
          color: chartTheme.title
        },
        datalabels: {
          display: true,
          color: chartTheme.dataLabel,
          anchor: 'end',
          align: 'end',
          formatter: (value: any) => value
        }
      }
    };
  }

  loadHighRiskChart(res: any){
    const chartTheme = this.getMedicationChartTheme();
    if (Array.isArray(res["medGroupTable"])) {
      const top10MedGroups = res["medGroupTable"]
        .filter((item: any) => typeof item.highRiskPercent === 'number' && item.medGroupName)
        .sort((a: any, b: any) => b.highRiskPercent - a.highRiskPercent)
        .slice(0, 10);

      this.highRiskPerc = {
        labels: top10MedGroups.map((item: any) => String(item.medGroupName)),
        datasets: [
          {
            label: 'High Risk %',
            data: top10MedGroups.map((item: any) => Number(item.highRiskPercent.toFixed(2))),
            backgroundColor: chartTheme.palette.slice(0, top10MedGroups.length),
            borderWidth: 1
          }
        ]
      };
    }
    this.highRiskOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: false,
          position: 'right'
        },
        title: {
          display: true,
          text: 'Medication Groups by High Risk %',
          color: chartTheme.title
        },
        datalabels: {
          display: true,
          color: chartTheme.dataLabel,
          anchor: 'end',
          align: 'end',
          clip: false,
          formatter: (value: any) => value + '%'
        }
      }
    };
  }

  loadUnaccountedChart(res: any){
    const chartTheme = this.getMedicationChartTheme();
    if (Array.isArray(res["medGroupTable"])) {
      const top10MedGroups = res["medGroupTable"]
        .filter((item: any) => typeof item.unaccountedCount === 'number' && item.medGroupName)
        .sort((a: any, b: any) => b.unaccountedCount - a.unaccountedCount)
        .slice(0, 10);

      this.unaccountedMed = {
        labels: top10MedGroups.map((item: any) => String(item.medGroupName)),
        datasets: [
          {
            label: 'Unaccounted',
            data: top10MedGroups.map((item: any) => Number(item.unaccountedCount)),
            backgroundColor: chartTheme.palette.slice(0, top10MedGroups.length),
            borderWidth: 1
          }
        ]
      };
    }
    this.unaccountedOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: false,
          position: 'right',
        },
        title: {
          display: true,
          text: 'Medication Groups by Unaccounted',
          color: chartTheme.title
        },
        datalabels: {
          display: true,
          color: chartTheme.dataLabel,
          anchor: 'end',
          align: 'end',
          clamp: true,
          clip: false,
          font: {
            weight: 'bold',
            size: 16
          },
          formatter: (value: any, context: any) => {
            const label = context.chart.data.labels[context.dataIndex];
            return label + '\n' + value;
          }
        }
      }
    };
  }

  loadVerifiedChart(res: any){
    const chartTheme = this.getMedicationChartTheme();
    if (Array.isArray(res["medGroupTable"])) {
      const top10MedGroups = res["medGroupTable"]
        .filter((item: any) => typeof item.verifiedCount === 'number' && item.medGroupName)
        .sort((a: any, b: any) => b.verifiedCount - a.verifiedCount)
        .slice(0, 10);

      this.verifiedCount = {
        labels: top10MedGroups.map((item: any) => String(item.medGroupName)),
        datasets: [
          {
            label: 'Unaccounted',
            data: top10MedGroups.map((item: any) => Number(item.verifiedCount)),
            backgroundColor: chartTheme.palette.slice(0, top10MedGroups.length),
            borderWidth: 1
          }
        ]
      };
    }
    this.verifiedOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: false,
          position: 'right'
        },
        title: {
          display: true,
          text: 'Medication Groups by Verified',
          color: chartTheme.title
        },
        datalabels: {
          display: true,
          color: chartTheme.dataLabel,
          anchor: 'end',
          align: 'end',
          formatter: (value: any) => value
        }
      }
    };
  }

  showRowData(row: any, column: string) {
    this.selectedVerifiedRow = row;

    const filter: any = {
      ...this.filter,
      medGroup: row.medGroupName,
      duration: this.selectedDays || this.filter.duration || 90,
      initializing: true,
      ia: true,
    };

    if (column === 'unaccountedCount') {
      filter.resolution = ["Unreconciled Dose: Multi-User", "Unreconciled Dose: Single-User"];
    } else if (column === 'highRiskPercent') {
      filter.riskLevels = 'highRisk';
    } else if (column === 'verifiedCount') {
      filter.transType = "Verified";
    }

    this.rowFilter = { ...filter };
    this.filterTable = true;
  }

  removeFilter() {
    this.filterTable = false;
  }

  openFilterModule(filterInputs: any) {
    if (this.varianceComp && this.varianceComp.openVarianceFilter) {
      this.varianceComp.openVarianceFilter(filterInputs);
    }
  }

  gdfs(s: string){
    const d = s.substring(0,10).split('-')
    return d[1] + '-' + d[2] + '-' + d[0]
  }

  updatedDate(filter: any){
    this.date.startDate = new Date(this.gdfs(filter.startDate)).setHours(0,0,0);
    this.date.endDate = new Date(this.gdfs(filter.endDate)).setHours(23,59,59);
  }

  onLocationChanged(event: any){
    this.loading=true;
    const sanitized = this.locationAccessService.sanitizeSelectedFilters(event, this.authService.user);
    this.locationSelectFilter = {
      ...sanitized,
      footprint: this.footprint,
      changeValue: event?.changeValue
    };
    this.locationAccessService.applyAuthorizedFilters(this.filter, sanitized, this.authService.user);

    if(this.locationAccessService.hasAll(sanitized.region)) {
        this.filter.adminId = []
        this.auditorSelection = {}
    }
    this.loc_filter_breakdown = this.locationAccessService.getFilterBreakdown(sanitized, {
        allLarge: 'Region',
        allSmall: 'Facility',
        regionSelected: 'Campus',
        campusSelected: 'Campus',
        facilitySelected: 'Facility',
        locationGroupSelected: 'LocationGrouping'
    }, this.footprint);
    this.filter.$sortOrder = -1;
    this.refreshData();
  }

  onLocationSelectInitialized(initialFilter: any): void {
    this.locationAccessService.applyAuthorizedFilters(this.filter, initialFilter, this.authService.user);
    this.refreshData();
  }

    onServiceGrpChanged(event: any){
    this.filter.serviceGroup = event.serviceGroup;
    this.loading = true;
    this.refreshData();
  }

    updateLookbackPeriod(event: any) {
    const lookbackPeriod = event["value"];
    this.filter.duration = lookbackPeriod;
    this.refreshData();
  }

  loadMedOptions(){
    this.loading=true;
    this.auditService.getMedSelect(this.filter.medOptions).subscribe((res) => {
      this.medGroupOptions = [{label: "All Med Group Names", value: "All"}].concat(
        (res as any)["medGroupSelect"].map((medGroupName: any) => {
          return {label: medGroupName.label, value: medGroupName.value}
        })
      );
      this.filter.medOptions = ["All"];
    })
    this.refreshData();
  }

  onMedGroupChange(event: any){
    const selected = event.value || [];
    // If nothing is selected, default to All
    if (selected.length === 0) {
      this.filter.medOptions = ["All"];
    }
    // If "All" is selected (alone)
    else if (selected.length === 1 && selected[0] === "All") {
      this.filter.medOptions = ["All"];
    }
    // If "All" is selected with others, remove "All"
    else if (selected.includes("All")) {
      this.filter.medOptions = selected.filter((v: any) => v !== "All");
    }
    // Otherwise, set to selected values
    else {
      this.filter.medOptions = selected;
    }
    this.refreshData();
  }

 
}
